import { ServiceContext } from '@confer/lib'
import type { Survey } from '@confer/lib'
import isPropValid from '@emotion/is-prop-valid'
import React, { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom'
import { StyleSheetManager } from 'styled-components'
import OutdatedBrowser from './components/pages/OutdatedBrowser'

import { services } from './services'

import '@confer/lib/assets/styles.css'
import SurveyPage from './components/pages/SurveyPage'
import LoadingSplash from './components/layout/LoadingSplash'
import RestorePage from './components/pages/RestorePage'
import { loadSurvey } from 'virtual:survey-loader'

// Dynamic Imports
const Home = lazy(() => import('./components/website/index'))
const AdminRouter = lazy(() => import('./components/dashboard/AdminRouter'))
// const SurveyWrapper = lazy(() => import('./components/app/SurveyWrapper'))
const NotFound = lazy(() => import('./components/pages/NotFound'))

const Router: React.FC = () => {
  useEffect(() => {
    services.userService.initialise()
    services.analyticsService.initialise()
    services.loggingService.initialise(services.crashReportingService)
    services.crashReportingService.initialise()
    return () => {
      services.userService.dispose()
      services.analyticsService.dispose()
      services.loggingService.dispose()
      services.crashReportingService.dispose()
    }
  }, [])
  return (
    /* fallback prop accepts any react element while waiting for component to load */
    <Suspense fallback={<LoadingSplash />}>
      <ServiceContext.Provider value={services}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
          <BrowserRouter>
            <Routes>
              <Route path="survey/:surveyName" element={<SurveyFinder />} />
              <Route path="restore" element={<RestorePage />} />
              <Route path="admin/*" element={<AdminRouter />} />
              <Route path="/" element={<Home />} />

              <Route path="/*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </StyleSheetManager>
      </ServiceContext.Provider>
    </Suspense>
  )
}

const SurveyFinder = () => {
  const { surveyName } = useParams()
  const [survey, setSurvey] = useState<Survey | 'loading' | 'not-found'>('loading')
  useEffect(() => {
    if (!surveyName) return

    getSurvey(surveyName).then(
      (currentSurvey) => {
        services.loggingService.debug('survey', currentSurvey)
        setSurvey(currentSurvey)
      },
      (e) => {
        const error = e as Error
        services.loggingService.error('Error getting survey', { error })
        setSurvey('not-found')
      },
    )
  }, [surveyName])
  if (window.MSCompatibleInfo != null) {
    console.log('internet explorer')
    return <OutdatedBrowser />
  }
  if (!surveyName) return <NotFound />
  if (survey === 'loading') return <LoadingSplash />
  if (survey === 'not-found') return <NotFound />
  return <SurveyPage survey={survey} />
}

async function getSurvey(surveyName: string): Promise<Survey> {
  try {
    const survey = await loadSurvey(surveyName)
    if (survey) return survey
    throw new Error(`Survey ${surveyName} not found`)
  } catch (_e) {
    throw new Error(`Survey ${surveyName} not found`)
  }
}

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

export default Router
